import type { OpRequest } from './ops';

export type HttpFetchInput = {
	url: string;
	access_token?: string | null;
	body?: unknown;
};

export type HttpInitConfig = {
	endpoint: string;
	token_provider: () => Promise<string | null>;
	fetch_fn: (input: HttpFetchInput) => Promise<{ data?: unknown; errors?: unknown[] } | null>;
	flush_interval_ms?: number;
};

let init: HttpInitConfig | null = null;
let warned_missing_endpoint = false;

export function init_http(config: HttpInitConfig) {
	init = config;
	if (!config.endpoint && !warned_missing_endpoint) {
		warned_missing_endpoint = true;
		console.warn('init_http: endpoint missing — HTTP transport will no-op until configured');
	}
}

export function reset_http_for_tests() {
	init = null;
	query_queue.clear();
	mutation_queue.clear();
	if (query_timer) {
		clearTimeout(query_timer);
		query_timer = undefined;
	}
	if (mutation_timer) {
		clearTimeout(mutation_timer);
		mutation_timer = undefined;
	}
}

export function http_initialized(): boolean {
	return init !== null;
}

type Waiter = { resolve: (value: any) => void; reject: (reason?: unknown) => void };
type QueueEntry = { op: OpRequest; waiters: Waiter[] };

const query_queue = new Map<string, QueueEntry>();
const mutation_queue = new Map<string, QueueEntry>();
let query_timer: ReturnType<typeof setTimeout> | undefined;
let mutation_timer: ReturnType<typeof setTimeout> | undefined;
let mutation_counter = 0;

function flush_interval(): number {
	return init?.flush_interval_ms ?? 20;
}

function schedule(kind: 'query' | 'mutation') {
	if (kind === 'mutation') {
		if (!mutation_timer) mutation_timer = setTimeout(() => void flush('mutation'), flush_interval());
	} else {
		if (!query_timer) query_timer = setTimeout(() => void flush('query'), flush_interval());
	}
}

export function enqueue(op: OpRequest): Promise<any> {
	if (!init) return Promise.resolve(null);

	if (op.kind === 'query') {
		const existing = query_queue.get(op.dedup_key);
		if (existing) {
			return new Promise<any>((resolve, reject) => {
				existing.waiters.push({ resolve, reject });
			});
		}
		return new Promise<any>((resolve, reject) => {
			query_queue.set(op.dedup_key, { op, waiters: [{ resolve, reject }] });
			schedule('query');
		});
	}

	mutation_counter += 1;
	return new Promise<any>((resolve, reject) => {
		mutation_queue.set(op.dedup_key + '#' + mutation_counter, { op, waiters: [{ resolve, reject }] });
		schedule('mutation');
	});
}

export function combine(
	kind: 'query' | 'mutation',
	ops: OpRequest[]
): { query: string; variables: Record<string, unknown> } {
	const declarations: string[] = [];
	const variables: Record<string, unknown> = {};
	for (const op of ops) {
		declarations.push(...op.declarations);
		Object.assign(variables, op.variables);
	}
	const header = declarations.length > 0 ? ` (${declarations.join(', ')})` : '';
	const body = ops.map((op) => op.query).join('\n  ');
	return { query: `${kind}${header} {\n  ${body}\n}`, variables };
}

async function flush(kind: 'query' | 'mutation') {
	const queue = kind === 'query' ? query_queue : mutation_queue;
	const entries = [...queue.values()];
	queue.clear();
	if (kind === 'query') {
		if (query_timer) {
			clearTimeout(query_timer);
			query_timer = undefined;
		}
	} else if (mutation_timer) {
		clearTimeout(mutation_timer);
		mutation_timer = undefined;
	}

	if (!init || entries.length === 0) return;

	const { query, variables } = combine(kind, entries.map((entry) => entry.op));
	let data: Record<string, unknown> | null = null;
	try {
		const access_token = await init.token_provider();
		const response = await init.fetch_fn({
			url: init.endpoint,
			access_token,
			body: { query, variables }
		});
		data = (response?.data ?? null) as Record<string, unknown> | null;
	} catch (error) {
		console.error('http flush threw', { query, error });
	}
	if (data === null) {
		console.error('http flush failed', { query });
	}
	for (const entry of entries) {
		const value = data === null ? null : (data[entry.op.alias] ?? null);
		for (const waiter of entry.waiters) waiter.resolve(value);
	}
}

export function pending_op_count(): number {
	return query_queue.size + mutation_queue.size;
}

export async function flush_now(): Promise<void> {
	await Promise.all([flush('query'), flush('mutation')]);
}
